import { type DetalhamentoMonitoramentoValue, type LocalMonitoramentoTipo } from './Components';
import { type UseCriarMonitoramentoFormInitialValues } from './hooks/useCriarMonitoramentoForm';
import { MonitoramentoDetalheDTO, MonitoramentoPeriodo, MonitoramentoTipo } from './types';

const mapLocalizacaoParaAnalise = (detalhe: MonitoramentoDetalheDTO): { tipoLocalizacao: string; listaLocalizacao: string[] } => {
  if (detalhe.todoTerritorio) {
    return { tipoLocalizacao: 'TODO_TERRITORIO', listaLocalizacao: [] };
  }

  const ufEntries = detalhe.cidades.filter(cidade => !cidade.cidadeNome && cidade.uf);
  if (ufEntries.length > 0) {
    return { tipoLocalizacao: 'UF', listaLocalizacao: ufEntries.map(cidade => cidade.uf) };
  }

  return { tipoLocalizacao: 'CIDADE', listaLocalizacao: detalhe.cidades.map(cidade => cidade.cidadeNome) };
};

const TIPO_PRECO_MAP: Record<string, string> = {
  REGULAR: 'PRECO_REGULAR',
  PAGO: 'PRECO_PAGO',
};

const TIPO_PRECO_REVERSE_MAP: Record<string, string> = {
  PRECO_REGULAR: 'REGULAR',
  PRECO_PAGO: 'PAGO',
};

const PERIODO_SEMANAS_MAP: Record<number, MonitoramentoPeriodo> = {
  4: MonitoramentoPeriodo.ULTIMAS_4_SEMANAS,
  8: MonitoramentoPeriodo.ULTIMAS_8_SEMANAS,
  12: MonitoramentoPeriodo.ULTIMAS_12_SEMANAS,
};

const parsePeriodoSemanas = (periodo: string): number => {
  const match = periodo.match(/\d+/);
  return match ? Number(match[0]) : 12;
};

export type AnaliseCompetitividadeFiltroRequest = {
  tipoLocalizacao: string;
  gtins: string[];
  tipoComparacao?: string;
  categorias?: string[];
  secoes?: string[];
  periodoSemanas: number;
  redeReferencia: string | null;
  redesCompetidoras: string[];
  listaLocalizacao: string[];
};

export type AnaliseCompetitividadeNavigationState = {
  competitivityFilterDTO: { request: AnaliseCompetitividadeFiltroRequest };
  preferences: { canalFisico: boolean; canalDigital: boolean; tipoPreco?: string };
};

export const getAnaliseCompetitividadeRoute = (tipo: MonitoramentoTipo): string =>
  tipo === MonitoramentoTipo.COMPETITIVIDADE_VAREJO ? '/isa/analise-competitividade/varejo' : '/isa/analise-competitividade/produtos';

export const mapMonitoramentoDetalheToAnaliseCompetitividade = (detalhe: MonitoramentoDetalheDTO): AnaliseCompetitividadeNavigationState => {
  const produtoReferencia = detalhe.produtos.find(produto => produto.tipo === 'REFERENCIA');
  const produtosConcorrentes = detalhe.produtos.filter(produto => produto.tipo === 'CONCORRENTE');
  const redeReferencia = detalhe.redes.find(rede => rede.tipo === 'REFERENCIA');
  const redesConcorrentes = detalhe.redes.filter(rede => rede.tipo === 'CONCORRENTE');
  const periodoSemanas = parsePeriodoSemanas(detalhe.periodo);
  const localizacao = mapLocalizacaoParaAnalise(detalhe);
  const tipoComparacao = detalhe.categorias.length > 0 ? 'CATEGORIA' : detalhe.secoes.length > 0 ? 'SECAO' : 'PRODUTO';

  const request: AnaliseCompetitividadeFiltroRequest =
    detalhe.tipo === MonitoramentoTipo.COMPETITIVIDADE_VAREJO
      ? {
          ...localizacao,
          gtins: tipoComparacao === 'PRODUTO' ? detalhe.produtos.map(produto => produto.produtoEan) : [],
          tipoComparacao,
          categorias: detalhe.categorias,
          secoes: detalhe.secoes,
          periodoSemanas,
          redeReferencia: redeReferencia?.redeNome ?? null,
          redesCompetidoras: redesConcorrentes.map(rede => rede.redeNome),
        }
      : {
          ...localizacao,
          gtins: produtosConcorrentes.map(produto => produto.produtoEan),
          periodoSemanas,
          redeReferencia: produtoReferencia?.produtoEan ?? null,
          redesCompetidoras: detalhe.redes.map(rede => rede.redeNome),
        };

  const tipoPreco = TIPO_PRECO_MAP[detalhe.tipoPreco];

  return {
    competitivityFilterDTO: { request },
    preferences: { canalFisico: detalhe.canalFisico, canalDigital: detalhe.canalDigital, ...(tipoPreco ? { tipoPreco } : {}) },
  };
};

export type AnaliseVarejoFiltros = {
  tipoLocalizacao: string | null;
  gtins: string[];
  // A Análise guarda o escopo de comparação atual (Produto/Categoria/Seção) e o valor selecionado
  // no campo correspondente (comparisonScopeConfig, AnaliseCompetitividade/utils.js) — sem isso, abrir
  // "Criar Monitoramento" a partir de um escopo Categoria/Seção perdia a seleção silenciosamente,
  // porque só gtins era lido.
  tipoComparacao: string | null;
  categorias: string[];
  secoes: string[];
  periodoSemanas: number;
  redeReferencia: string | null;
  redesCompetidoras: string[];
  listaLocalizacao: string[];
};

export type AnaliseIndustriaFiltros = {
  tipoLocalizacao: string | null;
  produtoReferencia: string | null;
  produtosCompetidores: string[];
  periodoSemanas: number;
  redes: string[];
  listaLocalizacao: string[];
};

export type AnaliseCompetitividadePreferences = {
  canalFisico: boolean;
  canalDigital: boolean;
  tipoPreco?: string;
};

const mapLocalizacao = (tipoLocalizacao: string | null, listaLocalizacao: string[]): Pick<DetalhamentoMonitoramentoValue, 'localTipo' | 'ufs' | 'cidades'> => {
  const localTipo = (tipoLocalizacao ?? 'TODO_TERRITORIO') as LocalMonitoramentoTipo;

  if (localTipo === 'UF') {
    return { localTipo, ufs: listaLocalizacao, cidades: [] };
  }

  if (localTipo === 'CIDADE') {
    // A Análise não guarda o UF de cada cidade selecionada, só o nome — manda uf vazio, mesmo
    // tratamento já usado pro caso de seleção por UF (aceito e contabilizado corretamente pelo back).
    return { localTipo, ufs: [], cidades: listaLocalizacao.map(cidadeNome => ({ cidadeNome, uf: '' })) };
  }

  return { localTipo: 'TODO_TERRITORIO', ufs: [], cidades: [] };
};

const buildVarejoDetalhamentoBase = (filtros: AnaliseVarejoFiltros) => {
  const redeBase = {
    redeReferencia: filtros.redeReferencia ? { redeNome: filtros.redeReferencia } : null,
    redesConcorrentes: filtros.redesCompetidoras.map(redeNome => ({ redeNome })),
  };

  if (filtros.tipoComparacao === 'CATEGORIA') {
    return { ...redeBase, produtos: [], tipoComparacao: 'CATEGORIA' as const, categorias: filtros.categorias, secoes: [] };
  }

  if (filtros.tipoComparacao === 'SECAO') {
    return { ...redeBase, produtos: [], tipoComparacao: 'SECAO' as const, categorias: [], secoes: filtros.secoes };
  }

  return {
    ...redeBase,
    // A Análise só guarda o GTIN dos produtos, sem nome — a criação só usa o EAN (buildProdutos),
    // então o nome fica vazio aqui; quem precisa exibir um nome legível resolve via catálogo à parte.
    produtos: filtros.gtins.map(gtin => ({ produtoEan: gtin, produtoNome: '' })),
    tipoComparacao: 'PRODUTO' as const,
    categorias: [],
    secoes: [],
  };
};

const buildIndustriaDetalhamentoBase = (filtros: AnaliseIndustriaFiltros) => {
  // A Análise não distingue rede "referência" de "concorrente" no modo indústria (todas ficam em
  // `redes`), mas a criação de monitoramento exige uma rede referência — usa a primeira da lista por
  // convenção, mesma lógica que o form completo já usa pra produtos em `buildProdutos`.
  const [redeReferenciaNome, ...redesConcorrentesNomes] = filtros.redes;

  return {
    produtos: [
      ...(filtros.produtoReferencia ? [{ produtoEan: filtros.produtoReferencia, produtoNome: '' }] : []),
      ...filtros.produtosCompetidores.map(gtin => ({ produtoEan: gtin, produtoNome: '' })),
    ],
    // Escopo categoria/seção não existe no modo indústria/produto — sempre Produto aqui.
    tipoComparacao: 'PRODUTO' as const,
    categorias: [],
    secoes: [],
    redeReferencia: redeReferenciaNome ? { redeNome: redeReferenciaNome } : null,
    redesConcorrentes: redesConcorrentesNomes.map(redeNome => ({ redeNome })),
  };
};

export const mapAnaliseCompetitividadeToMonitoramento = (
  tipo: MonitoramentoTipo,
  filtros: AnaliseVarejoFiltros | AnaliseIndustriaFiltros,
  preferences: AnaliseCompetitividadePreferences,
): UseCriarMonitoramentoFormInitialValues => {
  const detalhamentoBase =
    tipo === MonitoramentoTipo.COMPETITIVIDADE_VAREJO
      ? buildVarejoDetalhamentoBase(filtros as AnaliseVarejoFiltros)
      : buildIndustriaDetalhamentoBase(filtros as AnaliseIndustriaFiltros);

  return {
    monitoringType: tipo,
    detalhamento: {
      ...detalhamentoBase,
      ...mapLocalizacao(filtros.tipoLocalizacao, filtros.listaLocalizacao),
      periodo: PERIODO_SEMANAS_MAP[filtros.periodoSemanas] ?? MonitoramentoPeriodo.ULTIMAS_4_SEMANAS,
    },
    priceType: TIPO_PRECO_REVERSE_MAP[preferences.tipoPreco ?? ''] ?? 'PAGO',
    physicalChannel: preferences.canalFisico,
    digitalChannel: preferences.canalDigital,
  };
};
